import { Check } from "lucide-react";
import type { TimelineItem } from "@/lib/blocks";
import { cn } from "@/lib/utils";
import { Markdown } from "@/components/Markdown";

export function Timeline({ props }: { props: Record<string, unknown> }) {
  const items: TimelineItem[] = Array.isArray(props.items)
    ? (props.items as unknown[]).map((raw) => {
        const o = (raw && typeof raw === "object" ? raw : { title: String(raw) }) as TimelineItem;
        const status = o.status === "done" || o.status === "current" || o.status === "upcoming" ? o.status : undefined;
        return {
          date: o.date !== undefined && o.date !== null ? String(o.date) : undefined,
          title: String(o.title ?? ""),
          body: o.body !== undefined && o.body !== null ? String(o.body) : undefined,
          status,
        };
      })
    : [];

  if (items.length === 0) {
    return <div className="rounded-xl border border-dashed px-4 py-3 text-sm text-muted-foreground">Empty timeline</div>;
  }

  return (
    <ol className="relative ml-2 border-l border-border pl-6">
      {items.map((it, i) => (
        <li key={i} className={cn("relative pb-5 last:pb-0", it.status === "upcoming" && "text-muted-foreground")}>
          <Dot status={it.status} />
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            {it.date && <span className="font-mono text-xs uppercase tracking-wide text-muted-foreground">{it.date}</span>}
            <span className={cn("text-[0.95rem] font-semibold leading-snug", it.status === "current" && "text-foreground")}>
              <Markdown inline>{it.title}</Markdown>
            </span>
            {it.status === "current" && (
              <span className="rounded-full bg-tone-info/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tone-info">
                now
              </span>
            )}
          </div>
          {it.body && (
            <div className="mt-1 text-sm text-muted-foreground">
              <Markdown compact>{it.body}</Markdown>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

function Dot({ status }: { status?: TimelineItem["status"] }) {
  const base = "absolute -left-[31px] top-[0.3rem] flex size-[14px] items-center justify-center rounded-full border-2";
  if (status === "done") {
    return (
      <span className={cn(base, "border-primary bg-primary text-primary-foreground")} aria-label="done">
        <Check className="size-2.5" strokeWidth={4} />
      </span>
    );
  }
  if (status === "current") {
    return (
      <span className={cn(base, "border-tone-info bg-card")} aria-label="current">
        <span className="size-1.5 rounded-full bg-tone-info" />
      </span>
    );
  }
  if (status === "upcoming") {
    return <span className={cn(base, "border-border bg-card")} aria-label="upcoming" />;
  }
  return <span className={cn(base, "border-muted-foreground/50 bg-card")} />;
}
